import type { PlanEffectEnvelope, PlanPresentation, ScenePlan } from '@toolproof-core/lib/_contract/scene-plan';

import { isBeatStartCuePrepared, planAnchorIndex } from './episode';
import type { EpisodePlayback } from '@/_contract/episode';
import type { EpisodePresentationRequest } from '@/_contract/episodePresentation';
import type { TraceReplay } from '@/_contract/trace';

export function episodePresentationAnchorIndex<TEffect extends PlanEffectEnvelope>(
    plan: Pick<ScenePlan<TEffect>, 'beats'>, replay: TraceReplay, beatIndex: number,
    presentation: PlanPresentation<TEffect>, openingStepIndex = 0,
): number {
    const beat = plan.beats[beatIndex];
    if (!beat) throw new Error(`Episode presentation has no beat ${beatIndex}.`);
    if (presentation.afterCue !== undefined) {
        const cue = beat.cues?.find(cue => cue.id === presentation.afterCue);
        if (!cue) throw new Error(`Missing presentation source cue '${presentation.afterCue}'.`);
        return planAnchorIndex(replay, cue);
    }
    if (beat.replayFrom) return planAnchorIndex(replay, beat.replayFrom);
    if (beatIndex === 0) return openingStepIndex;
    const previous = plan.beats[beatIndex - 1];
    if (!previous) throw new Error('Episode presentation lost its preceding beat.');
    return planAnchorIndex(replay, previous);
}

export function selectTraceEpisodePresentation<TEffect extends PlanEffectEnvelope>(args: {
    plan: Pick<ScenePlan<TEffect>, 'beats'>; replay: TraceReplay;
    playback: EpisodePlayback; committedStepIndex: number; openingStepIndex?: number;
}): { selected: PlanPresentation<TEffect>; anchorIndex: number } | undefined {
    const { plan, replay, playback } = args;
    if (playback.error) return undefined;
    const beat = plan.beats[playback.beatIndex];
    if (!beat?.presentation) return undefined;
    let selected: PlanPresentation<TEffect> | undefined;
    for (const segment of beat.presentation) {
        const cueIndex = segment.afterCue === undefined ? -1 : beat.cues?.findIndex(cue => cue.id === segment.afterCue) ?? -1;
        if (segment.afterCue !== undefined && cueIndex < 0) {
            throw new Error(`Missing presentation source cue '${segment.afterCue}'.`);
        }
        if (segment.afterCue === undefined || (playback.cueIndex ?? 0) > cueIndex
            || isBeatStartCuePrepared(beat, playback, cueIndex)) selected = segment;
    }
    if (!selected || (selected.until && args.committedStepIndex >= planAnchorIndex(replay, selected.until))) return undefined;
    return { selected, anchorIndex: episodePresentationAnchorIndex(plan, replay, playback.beatIndex, selected, args.openingStepIndex) };
}

/** Playback activation is shared; adapters still validate stationary-only effects and resolve geometry. */
export function createEpisodePresentationRequest<TPresentation extends { id: string }>(
    presentation: TPresentation, playback: EpisodePlayback, stationary: boolean,
): EpisodePresentationRequest<TPresentation> {
    return {
        key: `${playback.episodeHandle}:${playback.beatIndex}:${playback.revision}:${presentation.id}`,
        presentation,
        orbitState: playback.visualRequested || playback.isPlaying || playback.narrationRequested
            ? stationary ? 'stationary' : 'moving' : 'inactive',
    };
}
